import React, { useState, useEffect } from "react";
import SystemAdminLayout from "../layouts/SystemAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Plus, Pencil, Trash2, Check, X, Loader2, 
  Handshake, ToggleLeft, ToggleRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnershipTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const response = await fetch(`${API}/partners/admin/partnership-types`);
      const data = await response.json();
      setTypes(data.partnership_types || []);
    } catch (error) {
      console.error('Error fetching partnership types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingType 
        ? `${API}/partners/admin/partnership-types/${editingType.id}`
        : `${API}/partners/admin/partnership-types`;
      
      const response = await fetch(url, {
        method: editingType ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setShowModal(false);
        setEditingType(null);
        setFormData({ name: '', description: '', is_active: true });
        fetchTypes();
      } else {
        alert(result.detail || 'Error saving partnership type');
      }
    } catch (error) {
      alert('Error saving partnership type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type) => {
    if (!confirm(`Are you sure you want to delete "${type.name}"?`)) return;

    try {
      const response = await fetch(`${API}/partners/admin/partnership-types/${type.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchTypes();
      } else {
        const result = await response.json();
        alert(result.detail || 'Error deleting partnership type');
      }
    } catch (error) {
      alert('Error deleting partnership type');
    }
  };

  const handleToggleActive = async (type) => {
    try {
      const response = await fetch(`${API}/partners/admin/partnership-types/${type.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !type.is_active })
      });

      if (response.ok) {
        fetchTypes();
      }
    } catch (error) {
      alert('Error updating status');
    }
  };

  const openEditModal = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      is_active: type.is_active !== false
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({ name: '', description: '', is_active: true });
    setShowModal(true);
  };

  return (
    <SystemAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Partnership Types</h1>
            <p className="text-gray-600 text-sm">Manage the types of partnerships available for partners</p>
          </div>
          <Button 
            onClick={openCreateModal}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            <Plus className="w-5 h-5 mr-2" /> Add Partnership Type
          </Button>
        </div>

        {/* Types List */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Handshake className="w-5 h-5 text-purple-500" />
              Partnership Types ({types.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" />
                <p className="text-gray-500">Loading...</p>
              </div>
            ) : types.length === 0 ? (
              <div className="text-center py-12">
                <Handshake className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No partnership types defined yet</p>
                <Button onClick={openCreateModal}>
                  <Plus className="w-4 h-4 mr-2" /> Create First Type
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {types.map((type) => (
                  <div 
                    key={type.id} 
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      type.is_active !== false ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        type.is_active !== false ? 'bg-purple-100' : 'bg-gray-200'
                      }`}>
                        <Handshake className={`w-5 h-5 ${
                          type.is_active !== false ? 'text-purple-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <div>
                        <h4 className={`font-semibold ${
                          type.is_active !== false ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {type.name}
                        </h4>
                        {type.description && (
                          <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleToggleActive(type)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={type.is_active !== false ? 'Deactivate' : 'Activate'}
                      >
                        {type.is_active !== false ? (
                          <ToggleRight className="w-6 h-6 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-gray-400" />
                        )}
                      </button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditModal(type)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(type)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setShowModal(false)} />
            <Card className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
              <CardHeader>
                <CardTitle>
                  {editingType ? 'Edit Partnership Type' : 'Add Partnership Type'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Strategic Partner"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe this partnership type..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Active (visible in dropdown)</Label>
                </div>
              </CardContent>
              <div className="p-6 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> {editingType ? 'Save Changes' : 'Create'}</>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </SystemAdminLayout>
  );
};

export default PartnershipTypesManagement;
